import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faComments,
  faUsers,
  faBell,
  faShareSquare,
  faCheckCircle,
  faRocket,
  faPlus,
  faBackward,
  faArrowLeft,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { Button } from "@/components/ui/button/Button";
import { useWorkerCreation } from "@/context/WorkerCreationContext";
import { useNavigate } from "react-router";
import WorkerAvatar from "@/components/worker/WorkerAvatar";
import integrationOptionsData from "./completion-integration-options.json";
import WorkerCard from "../WorkerCard";
import GradientBlobContainer from "@/components/ui/container/GradientBlobContainer";

const iconMap: { [key: string]: any } = {
  faComments,
  faUsers,
  faBell,
  faShareSquare,
};

const integrationOptions = integrationOptionsData.map((option) => ({
  ...option,
  icon: iconMap[option.icon],
}));

export default function CreateWorkerCompletion() {
  const { workerData, resetWorkerData } = useWorkerCreation();
  const navigate = useNavigate();
  const [showSlackFeatures, setShowSlackFeatures] = useState(false);

  const handleAddToPod = () => {
    // Reset worker creation data before navigating
    resetWorkerData();
    // Navigate to pod directory with the worker data
    navigate("/pod/directory", {
      state: {
        newWorker: {
          name: workerData.name,
          jobTitle: workerData.jobTitle,
          capabilities: Array.from(workerData.selectedCapabilities),
          bio: workerData.bio,
          id: workerData._id,
          rules: workerData.rules,
        },
      },
    });
  };

  const handleGoToWorkerDirectory = () => {
    // Reset worker creation data before navigating
    resetWorkerData();
    navigate("/worker/directory");
  };

  const handleConnectSlack = () => {
    setShowSlackFeatures((prev) => !prev);
  };

  const handleCreateAnotherWorker = () => {
    // Reset worker creation data and navigate back to template selection
    resetWorkerData();
    navigate("/worker/create");
  };

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Success Message */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-100 dark:bg-indigo-400/30 rounded-full mb-4">
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-3xl text-indigo-600 dark:text-indigo-400"
          />
        </div>
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-2">
          Worker Created Successfully!
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          {workerData.name} is ready to help you and your team.
        </p>
      </div>

      {/* Worker Summary */}
      <div className="bg-slate-50 max-w-lg relative overflow-hidden  mx-auto dark:bg-slate-800/50 rounded-2xl p-6  ring ring-inset ring-white/20 ">
        <div className="absolute bottom-0 top-0 left-0 right-0 w-full h-full z-10 bg-gradient-to-t from-slate-800 via-slate-800/90 to-slate-800/0 pointer-events-none"></div>

        <div className="flex items-center gap-4 mb-4 z-20 relative ">
          <WorkerAvatar seed={workerData.name} size={48} animated />
          <div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              {workerData.name}
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              {workerData.jobTitle}
            </p>
          </div>
        </div>
        <p className="text-slate-600 dark:text-slate-400 mb-4 z-20 relative">
          {workerData.bio}
        </p>
        <div className="flex flex-wrap gap-2 z-20 relative">
          {Array.from(workerData.selectedCapabilities).map((capability) => {
            // For now, use the fallback transformation since we don't have capability details in the creation context
            const displayName = capability
              .replace(/_/g, " ")
              .replace(/-/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase());

            return (
              <span
                key={capability}
                className="px-3 py-1 font-mono uppercase tracking-widest text-xs bg-indigo-100 dark:bg-indigo-400/10 text-indigo-700 dark:text-indigo-300 rounded-full "
              >
                {displayName}
              </span>
            );
          })}
        </div>
        <GradientBlobContainer
          seed={workerData.name}
          animated
          position="l"
          className="w-full h-full absolute inset-0 opacity-10 z-0"
        />
      </div>

      {/* What's Next Section */}
      <div>
        <h3 className="text-xl w-full text-centerfont-semibold text-slate-800 dark:text-slate-200 mb-6">
          What's Next?
        </h3>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Option 1: Add to Pod */}
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-6 border border-indigo-200 dark:border-indigo-700">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-indigo-100 dark:bg-indigo-900/30 p-2 rounded-lg">
                <FontAwesomeIcon
                  icon={faRocket}
                  className="text-xl text-indigo-600 dark:text-indigo-400"
                />
              </div>
              <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                Add to a Pod
              </h4>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Create or join a pod to coordinate {workerData.name} with other
              workers for complex workflows.
            </p>
            <Button onClick={handleAddToPod} color="indigo" className="w-full">
              <FontAwesomeIcon icon={faPlus} className="mr-2" />
              Add to Pod
            </Button>
          </div>

          {/* Option 2: Integrate with Slack */}
          <div className="bg-gradient-to-br from-emerald-50 to-blue-50 dark:from-emerald-900/20 dark:to-blue-900/20 rounded-2xl p-6 border border-emerald-400 dark:border-emerald-400">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-emerald-100 dark:bg-emerald-900/30 p-2 rounded-lg">
                <FontAwesomeIcon
                  icon={faComments}
                  className="text-xl text-emerald-600 dark:text-emerald-400"
                />
              </div>
              <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                Integrate with Slack
              </h4>
            </div>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Connect {workerData.name} to your Slack workspace for seamless
              team collaboration.
            </p>
            <Button
              onClick={handleConnectSlack}
              color="emerald"
              className="w-full"
            >
              Connect Slack
            </Button>
          </div>
        </div>
      </div>

      {/* Slack Integration Details */}
      {showSlackFeatures && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-6">
          <h4 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4">
            Slack Integration Features
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {integrationOptions.map((option) => (
              <div
                key={option.title}
                className="p-4 rounded-lg border border-slate-200 dark:border-slate-700 text-center flex flex-col items-center"
              >
                <FontAwesomeIcon
                  icon={option.icon}
                  className="text-2xl text-slate-500 dark:text-slate-400 mb-2"
                />
                <h5 className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                  {option.title}
                </h5>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {option.description}
                </p>
              </div>
            ))}
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg">
            <h5 className="font-semibold text-slate-800 dark:text-slate-200 mb-2">
              Setup Instructions
            </h5>
            <ol className="text-sm text-slate-600 dark:text-slate-400 space-y-1 list-decimal list-inside">
              <li>Install the Ani Slack app in your workspace.</li>
              <li>Authorize the connection with your Slack admin.</li>
              <li>
                Invite {workerData.name} to relevant channels using{" "}
                <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded text-xs">
                  @{workerData.name}
                </code>{" "}
                or{" "}
                <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded text-xs">
                  /invite-worker
                </code>
                .
              </li>
              <li>
                Start collaborating! {workerData.name} will respond to mentions
                and direct messages.
              </li>
            </ol>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button
          onClick={handleCreateAnotherWorker}
          color="indigo"
          className="flex-1 sm:flex-none"
        >
          <FontAwesomeIcon icon={faPlus} className="mr-2" />
          Create Another Worker
        </Button>
        <Button
          onClick={handleGoToWorkerDirectory}
          className="flex-1 sm:flex-none"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          View Worker Directory
        </Button>
      </div>
    </div>
  );
}
